import React from 'react';
import {Link} from '@forecast-it/design-system';
import {ReportRow} from '../../data/reportRowModel';
import {CellRenderer} from './CellRenderer';
import {TruncatedCellContent} from './TruncatedCellContent';
import {isModifiedClick} from './isModifiedClick';
import {MODAL_TYPE, showModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';

const TASK_NAME_PATH = 'task.name';

/** The task-name cell, rendered as the way into the task's modal.
 *
 * The modal is opened directly rather than through the app's usual URL-based helper, which
 * appends `/T{id}` to the current path: on this page that path is matched as the builder's
 * saved-report route, which would unmount the builder and try to load a report by that name.
 * Opening the modal in place leaves the URL — and an unsaved draft — untouched.
 *
 * `/T{id}` is nonetheless a genuine app route (it opens the same modal over the home page), so
 * it is used as the anchor's `href`: a cmd/ctrl/shift-click then opens a working tab instead of
 * a dead link, and the browser's own link affordances all behave.
 *
 * A row that is not a task (a deliverable, or a synthetic grouping header) has no modal to open
 * and renders as an ordinary cell. */
export function TaskNameCell({row}: {row: ReportRow}): JSX.Element {
	const value = row.cells[TASK_NAME_PATH];
	const {companyTaskId} = row;

	if (companyTaskId === null || value === undefined || value.kind !== 'text') {
		return <CellRenderer value={value} />;
	}

	const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
		if (isModifiedClick(event)) {
			return;
		}
		event.preventDefault();
		// `closeOtherModals` matches how the app's own `/T{id}` route opens this modal: a task modal
		// replaces whatever else is open rather than stacking on top of it, so closing it returns the
		// user to the table instead of to a modal they had already finished with.
		//
		// `openedInPlace` is what tells the modal it does not live in the URL. Without it the modal
		// cannot know: it would read `window.location` to decide how to close, and its own internal
		// links (breadcrumb parent, subtask rows, dependencies) would push `/T{id}` onto this page's
		// path — which drops the router state this report may be the only copy of.
		showModal({type: MODAL_TYPE.TASK_MODAL, taskId: String(companyTaskId), openedInPlace: true}, true);
	};

	return (
		<TruncatedCellContent tooltip={value.value}>
			<Link href={`/T${companyTaskId}`} onClick={handleClick} size={'2'}>
				{value.value}
			</Link>
		</TruncatedCellContent>
	);
}
